/**
 * vault_controller.js — document viewer for the vault detail page (Stimulus).
 *
 * Ports the viewer half of static/js/vault.js to an element-scoped controller on
 * #vault-viewer (data-controller="vault"). connect() renders the document (PDF via
 * PDF.js, image via <img>, else a download-fallback card); disconnect() and a
 * connect-scoped turbo:before-cache reset the element to its placeholder so the
 * cached snapshot shows the loading slot, not a stale canvas. This replaces the
 * old dataset-flag double-init guard + turbo:before-cache handler.
 *
 * PDF.js (pdfjsLib) is a page vendor script; on a Turbo navigation it may not be
 * defined when this controller connects, so for PDFs we defer the first render to
 * turbo:load when it's missing (the chart_controller deferral pattern).
 *
 * The delete-confirm (window.vaultConfirmDelete) stays in vault.js — it carries no
 * Turbo-lifecycle pattern and is a plain call-fn entry point.
 */

import { Controller } from "@hotwired/stimulus";

    /** Render a PDF using PDF.js — all pages stacked vertically. */
    function renderPdf(el, fileUrl, downloadUrl) {
        if (typeof pdfjsLib === 'undefined') {
            renderFallback(el, downloadUrl);
            return;
        }

        pdfjsLib.GlobalWorkerOptions.workerSrc = '/static/vendor/pdfjs/pdf.worker.min.js';

        var loading = document.createElement('p');
        loading.className = 'text-center text-muted py-4';
        loading.innerHTML =
            '<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>' +
            'Loading PDF…';
        el.appendChild(loading);

        pdfjsLib.getDocument(fileUrl).promise.then(function (pdfDoc) {
            el.innerHTML = '';
            var containerWidth = el.offsetWidth || 760;
            var numPages = pdfDoc.numPages;
            /* Render at the screen's pixel density so the canvas isn't upscaled
             * (blurry) on hi-DPI phones; cap at 2 to bound page memory. */
            var dpr = Math.min(window.devicePixelRatio || 1, 2);

            /* Render pages sequentially to avoid out-of-order canvas appends. */
            function renderPage(num) {
                pdfDoc.getPage(num).then(function (page) {
                    var defaultVp = page.getViewport({ scale: 1.0 });
                    var cssScale = containerWidth / defaultVp.width;
                    var viewport = page.getViewport({ scale: cssScale * dpr });

                    var canvas = document.createElement('canvas');
                    canvas.width = viewport.width;            // hi-DPI backing store
                    canvas.height = viewport.height;
                    canvas.style.width = (viewport.width / dpr) + 'px';   // lay out at CSS size
                    canvas.style.height = (viewport.height / dpr) + 'px';
                    el.appendChild(canvas);

                    page.render({ canvasContext: canvas.getContext('2d'), viewport: viewport }).promise.then(function () {
                        if (num < numPages) renderPage(num + 1);
                    });
                });
            }

            renderPage(1);
        }).catch(function () {
            el.innerHTML = '';
            renderFallback(el, downloadUrl);
        });
    }

    /** Render an image file with a native <img>. */
    function renderImage(el, fileUrl, downloadUrl) {
        var img = document.createElement('img');
        img.alt = 'Document preview';
        img.onerror = function () {
            el.innerHTML = '';
            renderFallback(el, downloadUrl);
        };
        img.src = fileUrl;
        el.appendChild(img);
    }

    /** Render "Preview not available" card with a download button. `download`
     *  keeps Turbo off the link and lets the iOS shell download from the tap. */
    function renderFallback(el, downloadUrl) {
        var card = document.createElement('div');
        card.className = 'vault-no-preview';
        card.innerHTML =
            '<i class="bi bi-file-earmark-x vault-no-preview-icon"></i>' +
            '<p class="vault-no-preview-msg">Preview not available yet — download to view</p>' +
            '<a href="' + downloadUrl + '" download class="btn btn-outline-primary btn-sm">' +
            '<i class="bi bi-download me-2"></i>Download file</a>';
        el.appendChild(card);
    }

    var PLACEHOLDER =
        '<span class="text-muted"><i class="bi bi-hourglass me-2"></i>Viewer loads here</span>';

export default class extends Controller {
        connect() {
            var mimeType = this.element.dataset.mimeType || '';
            // PDFs need pdfjsLib; on a Turbo nav it may not have executed yet.
            if (mimeType === 'application/pdf' && typeof pdfjsLib === 'undefined') {
                this._onLoad = () => this._render();
                document.addEventListener('turbo:load', this._onLoad, { once: true });
            } else {
                this._render();
            }

            this._onBeforeCache = () => this._reset();
            document.addEventListener('turbo:before-cache', this._onBeforeCache);
        }

        disconnect() {
            if (this._onLoad) {
                document.removeEventListener('turbo:load', this._onLoad);
                this._onLoad = null;
            }
            if (this._onBeforeCache) {
                document.removeEventListener('turbo:before-cache', this._onBeforeCache);
                this._onBeforeCache = null;
            }
            this._reset();
        }

        _render() {
            this._onLoad = null;
            var el = this.element;
            var mimeType = el.dataset.mimeType || '';
            var fileUrl = el.dataset.fileUrl || '';
            var downloadUrl = el.dataset.downloadUrl || '';

            el.innerHTML = '';
            el.classList.remove('vault-viewer-slot');

            if (mimeType === 'application/pdf') {
                renderPdf(el, fileUrl, downloadUrl);
            } else if (mimeType.startsWith('image/')) {
                renderImage(el, fileUrl, downloadUrl);
            } else {
                renderFallback(el, downloadUrl);
            }
        }

        _reset() {
            this.element.innerHTML = PLACEHOLDER;
            this.element.classList.add('vault-viewer-slot');
        }
    }
